import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link } from 'react-router';
import RoundedButtonPrimary from '../../../components/button/rounded-button-primary/RoundedButtonPrimary';
import ErrorMessage from '../../../components/info-messages/ErrorMessage';
import Input2 from '../../../components/input/input-2/Input2';
import LoaderBar from '../../../components/loader-bar/LoaderBar';
import { ContentTitle, SectionTitle } from '../../../components/typography/Heading';
import UserOrOrganizationBox from '../../../components/user-or-organization-box/UserOrOrganizationBox';
import { showErrorModal, showSuccessModal } from '../../../global-message/global-message-actions';
import { trackPageView } from '../../../support-and-tracking/analytics-actions';
import { formatMoneyKr } from '../../../utils';
import {
    fetchMatchingGiftsForOrganizationProjects,
    getOrganizationByOrganizationNumber,
    storeMatchingGift,
} from '../../organization-actions';
import OrganizationSearch from '../../organization-search/OrganizationSearch';
import { useTitle } from 'hoofd';
import { useBackgroundColor } from '../useBackgroundColor';
import styles from './matching-gift.module.less';

const MatchingGiftWhereOrganizationIsReceiver = () => {
    const params = useParams();
    const dispatch = useDispatch();
    const { organizationId } = params;

    const [projectId, setProjectId] = useState('');
    const [maxAmount, setMaxAmount] = useState('');
    const [email, setEmail] = useState('');
    const [organization, setOrganization] = useState(null);
    const [formError, setFormError] = useState();

    const fetchingMatchingGifts = useSelector((state) => state.organizations?.isFetchingMatchingGifts);
    const matchingGifts = useSelector((state) => state.organizations?.matchingGifts);
    const errors = useSelector((state) => state.organizations?.errors);

    const handleOrganizationSelection = useCallback((org) => {
        const { value, label } = org;
        dispatch(getOrganizationByOrganizationNumber(value))
            .then((org) => {
                setOrganization({ id: org.id, name: label, imgUrl: org.img_url });
            })
            .catch(() => {
                setOrganization(null);
            });
    }, []);

    useEffect(() => {
        trackPageView({
            urlAsEvent: 'organization-matching-gift',
            properties: { organizationId },
        });
    }, []);

    useEffect(() => {
        dispatch(fetchMatchingGiftsForOrganizationProjects({ organizationId }));
    }, [organizationId]);

    useTitle('Matching gifts');
    useBackgroundColor();

    if (!matchingGifts && fetchingMatchingGifts) {
        return <LoaderBar />;
    }

    const valid = () => {
        return (
            !!projectId &&
            !!maxAmount &&
            organization?.id &&
            !!email &&
            /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(email)
        );
    };
    const saveMatchingGift = (e) => {
        e.preventDefault();
        if (!valid()) {
            return dispatch(
                showErrorModal('Du må fylle ut prosjekt-id, maksbeløp, organisasjon og en gyldig e-postadresse')
            );
        }

        dispatch(
            storeMatchingGift({
                projectId,
                maxAmount,
                giverId: organization.id,
                receivingOrganizationId: organizationId,
                email,
            })
        )
            .then(() => {
                dispatch(fetchMatchingGiftsForOrganizationProjects({ organizationId }));
                setProjectId('');
                setOrganization(null);
                setMaxAmount('');
                setEmail('');
                dispatch(showSuccessModal('Matching gift lagret'));
            })
            .catch((err) => {
                setFormError(err.body?.errors);
                return dispatch(showErrorModal(err.body?.errors?.global || 'Klarte ikke å lagre matching gift'));
            });
    };

    const matchingGiftTable =
        matchingGifts?.length > 0 ? (
            <table className={styles.matchingGiftList}>
                <thead>
                    <tr>
                        <th className={styles.matchingGiftHeader}>Prosjekt</th>
                        <th className={styles.matchingGiftHeader}>Organisasjon</th>
                        <th className={styles.matchingGiftHeader}>Maksbeløp</th>
                        <th className={styles.matchingGiftHeader}>Betalt</th>
                    </tr>
                </thead>
                <tbody>
                    {matchingGifts.map((m) => (
                        <tr className={styles.matchingGift} key={m.id}>
                            <td className={styles.matchingGiftField}>
                                <Link to={`/project/${m.projectId}`}>{m.projectTitle}</Link>
                            </td>
                            <td className={styles.matchingGiftField}>
                                <Link to={`/org/${m.organization?.id}`}>{m.organization?.name}</Link>
                            </td>
                            <td className={styles.matchingGiftField}>{formatMoneyKr(m.maxAmount)}</td>
                            <td className={styles.matchingGiftField}>
                                {m.paid ? <i className={`far fa-check`} /> : <i className={`fa-solid fa-xmark`} />}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        ) : (
            <div>Det er ingen matching gifts til deres spleiser</div>
        );

    return (
        <div className={styles.container}>
            <div className={styles.wrapper}>
                <ContentTitle tag="h1">Matching gifts</ContentTitle>

                {matchingGiftTable}
                {!!errors && <ErrorMessage>{errors.global}</ErrorMessage>}
            </div>

            <div className={styles.matchGiftForm}>
                <form onSubmit={saveMatchingGift}>
                    <SectionTitle>Opprett matching gift</SectionTitle>
                    <Input2
                        required={true}
                        fieldName="projectId"
                        value={projectId}
                        title={'Prosjekt'}
                        type={'number'}
                        onChange={(e) => setProjectId(e.target.value)}
                        errors={formError}
                        className={styles.inputField}
                    />
                    <Input2
                        required={true}
                        fieldName="maxAmount"
                        value={maxAmount}
                        title={'Maks beløp'}
                        type={'number'}
                        onChange={(e) => setMaxAmount(e.target.value)}
                        errors={formError}
                        className={styles.inputField}
                    />
                    {!organization?.name && (
                        <OrganizationSearch
                            fieldName="organization"
                            handleOrganizationSelection={handleOrganizationSelection}
                            hidePromotedOrganizations={true}
                            handlePayoutToChange={() => {}}
                        />
                    )}
                    {!!organization?.name && (
                        <UserOrOrganizationBox
                            onClick={() => setOrganization(null)}
                            imgUrl={organization.imgUrl}
                            name={organization.name}
                        />
                    )}
                    <Input2
                        required={true}
                        fieldName="email"
                        value={email}
                        title={'E-postadresse'}
                        type={'email'}
                        onChange={(e) => setEmail(e.target.value)}
                        errors={formError}
                        className={styles.inputField}
                    />

                    <RoundedButtonPrimary>Lagre</RoundedButtonPrimary>
                </form>
            </div>
        </div>
    );
};

export default MatchingGiftWhereOrganizationIsReceiver;
